import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import ReactECharts from "echarts-for-react";
import {
  Shield,
  CheckCircle,
  AlertTriangle,
  Clock,
  FileText,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const GovernanceDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const response = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error("Error fetching governance data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Calculate governance metrics
  const approvedProjects = projects.filter(p => p.approval_status === 'approved').length;
  const pendingProjects = projects.filter(p => p.approval_status === 'pending').length;
  const complianceRate = projects.length > 0 ? Math.round(approvedProjects / projects.length * 100) : 0;

  // Demo compliance framework data
  const compliance_frameworks = {
    'COBIT': { compliant: 45, total: 50 },
    'King IV': { compliant: 38, total: 42 },
    'PFMA': { compliant: 28, total: 35 },
    'ISO 21500': { compliant: 40, total: 45 },
    'ESG Standards': { compliant: 32, total: 40 }
  };

  // Demo audit logs
  const audit_logs = [
    { action: 'approval', user: 'Johan van der Berg', entity: 'Lagos Solar Farm', timestamp: new Date().toISOString() },
    { action: 'create', user: 'Grace Mwangi', entity: 'New Project Submission', timestamp: new Date(Date.now() - 3600000).toISOString() },
    { action: 'update', user: 'Ahmed Hassan', entity: 'Cairo Metro Budget', timestamp: new Date(Date.now() - 7200000).toISOString() },
    { action: 'login', user: 'Fatou Diallo', entity: 'System Access', timestamp: new Date(Date.now() - 10800000).toISOString() },
    { action: 'approval', user: 'Kwame Asante', entity: 'Accra Port Phase 2', timestamp: new Date(Date.now() - 14400000).toISOString() },
    { action: 'update', user: 'Carlos Machel', entity: 'Risk Assessment', timestamp: new Date(Date.now() - 18000000).toISOString() },
    { action: 'create', user: 'Desta Kebede', entity: 'Compliance Report', timestamp: new Date(Date.now() - 21600000).toISOString() },
  ];

  const complianceHeatmapOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    radar: {
      indicator: [
        { name: 'COBIT', max: 100 },
        { name: 'King IV', max: 100 },
        { name: 'PFMA', max: 100 },
        { name: 'ISO 21500', max: 100 },
        { name: 'GDPR', max: 100 },
        { name: 'POPIA', max: 100 }
      ],
      axisName: { color: '#94a3b8' },
      splitLine: { lineStyle: { color: '#1e293b' } },
      splitArea: { areaStyle: { color: ['rgba(30, 41, 59, 0.3)', 'rgba(30, 41, 59, 0.5)'] } }
    },
    series: [{
      type: 'radar',
      data: [
        {
          value: [92, 88, 85, 90, 95, 93],
          name: 'Compliance Score',
          areaStyle: { color: 'rgba(34, 197, 94, 0.3)' },
          lineStyle: { color: '#22c55e', width: 2 },
          itemStyle: { color: '#22c55e' }
        }
      ]
    }]
  };

  const auditScheduleOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { color: '#94a3b8' }
    },
    series: [
      {
        name: 'Scheduled',
        type: 'bar',
        stack: 'total',
        data: [4, 3, 5, 4, 6, 4],
        itemStyle: { color: '#3b82f6' }
      },
      {
        name: 'Completed',
        type: 'bar',
        stack: 'total',
        data: [4, 3, 5, 3, 4, 2],
        itemStyle: { color: '#22c55e' }
      }
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading governance dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="governance-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Governance & Audit Dashboard
        </h1>
        <p className="text-slate-400 mt-1">Ensure governance compliance and audit readiness</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Approved Projects</p>
                <p className="text-3xl font-bold text-[#c9a227] mt-2">{approvedProjects}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#c9a227]/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#c9a227]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Compliance Rate</p>
                <p className="text-3xl font-bold text-green-400 mt-2">{complianceRate}%</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Pending Approvals</p>
                <p className="text-3xl font-bold text-yellow-400 mt-2">{pendingProjects}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Risk Status</p>
                <Badge className="mt-2 status-green text-lg">Healthy</Badge>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <Eye className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Compliance Heatmap</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={complianceHeatmapOptions} style={{ height: '350px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Audit Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={auditScheduleOptions} style={{ height: '350px' }} />
          </CardContent>
        </Card>
      </div>

      {/* Compliance Status & Audit Logs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Framework Compliance */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Framework Compliance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(compliance_frameworks).map(([framework, stats]) => (
              <div key={framework} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{framework}</span>
                  <span className="text-[#c9a227]">
                    {stats.compliant}/{stats.total}
                  </span>
                </div>
                <Progress 
                  value={(stats.compliant / stats.total) * 100} 
                  className="h-2 bg-slate-700"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Audit Logs */}
        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-slate-200">Recent Audit Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {audit_logs?.slice(0, 10).map((log, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-sm">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    log.action === 'login' ? 'bg-blue-400' :
                    log.action === 'create' ? 'bg-green-400' :
                    log.action === 'update' ? 'bg-yellow-400' : 'bg-slate-400'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-white font-medium capitalize">{log.action}</p>
                      <span className="text-xs text-slate-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400">{log.user} - {log.entity}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Governance Scorecard */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Governance Scorecard</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { name: 'Board Oversight', score: 95, status: 'green' },
              { name: 'Risk Management', score: 88, status: 'green' },
              { name: 'Internal Controls', score: 92, status: 'green' },
              { name: 'Transparency', score: 90, status: 'green' },
              { name: 'Accountability', score: 85, status: 'green' },
              { name: 'Audit Readiness', score: 93, status: 'green' }
            ].map((item) => (
              <div key={item.name} className="text-center p-4 bg-slate-800/30 rounded-sm">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                  item.score >= 90 ? 'bg-green-500/20' : item.score >= 70 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                }`}>
                  <span className={`text-2xl font-bold ${
                    item.score >= 90 ? 'text-green-400' : item.score >= 70 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {item.score}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-2">{item.name}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GovernanceDashboard;
